import { defineComponent as _defineComponent } from 'vue';
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = ["data-doc", "data-page"];
const _hoisted_2 = ["src"];
const _hoisted_3 = {
    key: 2,
    class: "doc-page__placeholder"
};
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { pdfjs } from "@/utils/pdfjs";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DocumentPage',
    props: {
        docKey: {},
        pageNumber: {},
        kind: {},
        url: {},
        size: {},
        scale: {},
        rotation: {},
        hits: {},
        root: {},
        getProxy: { type: Function },
        getText: { type: Function }
    },
    emits: ["current-mark"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const pageEl = ref(null);
        const canvasEl = ref(null);
        const textEl = ref(null);
        const rendered = ref(false);
        const visible = ref(false);
        const quarterTurn = computed(() => props.rotation % 180 !== 0);
        const width = computed(() => Math.round((quarterTurn.value ? props.size.height : props.size.width) * props.scale));
        const height = computed(() => Math.round((quarterTurn.value ? props.size.width : props.size.height) * props.scale));
        const pageStyle = computed(() => ({
            width: `${width.value}px`,
            height: `${height.value}px`,
            "--scale-factor": String(props.scale),
        }));
        const imageStyle = computed(() => {
            const w = props.size.width * props.scale;
            const h = props.size.height * props.scale;
            return {
                width: `${w}px`,
                height: `${h}px`,
                transform: `translate(${(width.value - w) / 2}px, ${(height.value - h) / 2}px) rotate(${props.rotation}deg)`,
            };
        });
        let observer = null;
        let renderTask = null;
        let textTask = null;
        let textDivs = [];
        let itemStrings = [];
        let drawnKey = "";
        let drawing = null;
        async function draw() {
            if (props.kind !== "pdf" || !visible.value)
                return;
            const key = `${props.scale}:${props.rotation}`;
            if (key === drawnKey)
                return;
            const proxy = props.getProxy(props.docKey);
            if (!proxy || !canvasEl.value || !textEl.value)
                return;
            renderTask?.cancel();
            drawnKey = key;
            const page = await proxy.getPage(props.pageNumber);
            if (drawnKey !== key || !visible.value)
                return;
            renderTask?.cancel();
            const viewport = page.getViewport({ scale: props.scale, rotation: props.rotation });
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            const canvas = canvasEl.value;
            canvas.width = Math.floor(viewport.width * ratio);
            canvas.height = Math.floor(viewport.height * ratio);
            const context = canvas.getContext("2d");
            if (!context)
                return;
            renderTask = page.render({
                canvasContext: context,
                viewport,
                transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
            });
            try {
                await renderTask.promise;
            }
            catch {
                if (drawnKey === key)
                    drawnKey = "";
                return;
            }
            if (drawnKey !== key || !textEl.value)
                return;
            const textContent = await page.getTextContent();
            if (drawnKey !== key || !textEl.value)
                return;
            textTask?.cancel();
            textEl.value.replaceChildren();
            const divs = [];
            const strings = [];
            const task = pdfjs.renderTextLayer({
                textContentSource: textContent,
                container: textEl.value,
                viewport,
                textDivs: divs,
                textContentItemsStr: strings,
            });
            textTask = task;
            try {
                await task.promise;
            }
            catch {
                return;
            }
            if (drawnKey !== key)
                return;
            textDivs = divs;
            itemStrings = strings;
            rendered.value = true;
            await paintHits();
        }
        function release() {
            renderTask?.cancel();
            textTask?.cancel();
            drawnKey = "";
            rendered.value = false;
            textDivs = [];
            itemStrings = [];
            textEl.value?.replaceChildren();
            if (canvasEl.value) {
                canvasEl.value.width = 0;
                canvasEl.value.height = 0;
            }
        }
        function redraw() {
            drawing = draw();
        }
        async function paintHits() {
            if (props.kind !== "pdf" || !textDivs.length)
                return;
            textDivs.forEach((div, index) => {
                if (div.childElementCount)
                    div.textContent = itemStrings[index];
            });
            if (!props.hits.length)
                return;
            const { starts, lengths } = await props.getText(props.docKey, props.pageNumber);
            let currentMark = null;
            const ranges = new Map();
            for (const hit of props.hits) {
                starts.forEach((start, index) => {
                    const from = Math.max(hit.start, start) - start;
                    const to = Math.min(hit.end, start + lengths[index]) - start;
                    if (to > from)
                        ranges.set(index, [...(ranges.get(index) ?? []), { from, to, current: hit.current }]);
                });
            }
            ranges.forEach((parts, index) => {
                const div = textDivs[index];
                const text = itemStrings[index];
                if (!div || text == null)
                    return;
                div.replaceChildren();
                let cursor = 0;
                for (const part of parts.sort((a, b) => a.from - b.from)) {
                    if (part.from > cursor)
                        div.append(text.slice(cursor, part.from));
                    const mark = document.createElement("mark");
                    mark.className = part.current ? "doc-hit doc-hit--current" : "doc-hit";
                    mark.textContent = text.slice(part.from, part.to);
                    div.append(mark);
                    if (part.current && !currentMark)
                        currentMark = mark;
                    cursor = part.to;
                }
                if (cursor < text.length)
                    div.append(text.slice(cursor));
            });
            if (currentMark)
                emit("current-mark", currentMark);
        }
        watch(() => [props.scale, props.rotation], () => redraw());
        watch(() => props.hits, async () => {
            await drawing;
            await paintHits();
        }, { deep: true });
        onMounted(() => {
            if (props.kind === "image")
                return;
            observer = new IntersectionObserver((entries) => {
                const intersecting = entries[entries.length - 1].isIntersecting;
                visible.value = intersecting;
                if (intersecting)
                    redraw();
                else
                    release();
            }, { root: props.root, rootMargin: "900px 0px" });
            nextTick(() => pageEl.value && observer?.observe(pageEl.value));
        });
        onBeforeUnmount(() => {
            observer?.disconnect();
            renderTask?.cancel();
            textTask?.cancel();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "pageEl",
                ref: pageEl,
                class: _normalizeClass(["doc-page", { 'doc-page--ready': rendered.value }]),
                style: _normalizeStyle(pageStyle.value),
                "data-doc": __props.docKey,
                "data-page": __props.pageNumber
            }, [
                (__props.kind === 'image')
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        class: "doc-page__image",
                        src: __props.url,
                        style: _normalizeStyle(imageStyle.value),
                        alt: "",
                        draggable: "false",
                        onLoad: _cache[0] || (_cache[0] = ($event) => (rendered.value = true))
                    }, null, 44, _hoisted_2))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createElementVNode("canvas", {
                            ref_key: "canvasEl",
                            ref: canvasEl,
                            class: "doc-page__canvas"
                        }, null, 512),
                        _createElementVNode("div", {
                            ref_key: "textEl",
                            ref: textEl,
                            class: "textLayer"
                        }, null, 512)
                    ], 64)),
                (!rendered.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
                            _createElementVNode("span", { class: "doc-page__line" }, null, -1),
                            _createElementVNode("span", { class: "doc-page__line doc-page__line--short" }, null, -1),
                            _createElementVNode("span", { class: "doc-page__line" }, null, -1)
                        ]))]))
                    : _createCommentVNode("", true)
            ], 14, _hoisted_1));
        };
    }
});
